import { ChevronDown, ExternalLink, HardDrive, Search } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleDriveFindFile } from 'sprites-types/workflow/tools/googleDriveFindFile'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function FileItem({ file, className }: { file: any; className?: string }) {
  const fileName = file.name || 'Untitled'
  const fileId = file.id
  const fileUrl =
    file.webViewLink ||
    file.url ||
    (fileId ? `https://drive.google.com/file/d/${fileId}/view` : null)
  const mimeType = file.mimeType
  const modifiedTime = file.modifiedTime
  const size = file.size

  const getFileIcon = (mimeType: string) => {
    if (mimeType?.includes('document')) return '📄'
    if (mimeType?.includes('spreadsheet')) return '📊'
    if (mimeType?.includes('presentation')) return '📈'
    if (mimeType?.includes('image')) return '🖼️'
    if (mimeType?.includes('video')) return '🎥'
    if (mimeType?.includes('audio')) return '🎵'
    if (mimeType?.includes('pdf')) return '📕'
    if (mimeType?.includes('folder')) return '📁'
    if (mimeType?.includes('text')) return '📝'
    return '📎'
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="flex items-center gap-4 px-4 py-3">
        <div className="flex-shrink-0">
          <span className="text-lg">{getFileIcon(mimeType || '')}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-gray-900">{fileName}</div>
          <div className="mt-1 flex items-center gap-4 text-xs text-gray-500">
            {fileId && <div>ID: {fileId.slice(0, 8)}...</div>}
            {size && <div>{parseInt(size).toLocaleString()} bytes</div>}
            {modifiedTime && <div>Modified: {new Date(modifiedTime).toLocaleDateString()}</div>}
          </div>
        </div>
        {fileUrl && (
          <div className="flex-shrink-0">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

function GoogleDriveFindFileDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleDriveFindFile, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const files = Array.isArray(result) ? result : (result as any)?.files || (result ? [result] : [])

  if (files.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No files found in Google Drive</div>
        </div>
      </div>
    )
  }

  if (files.length === 1) {
    return <FileItem file={files[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <HardDrive className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Google Drive Files</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {files.length} files found
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {files.map((file: unknown, index: number) => (
            <FileItem key={(file as any).id || index} file={file} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadGoogleDriveFindFile(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleDriveFindFileToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleDriveFindFile, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const files = Array.isArray(result) ? result : (result as any)?.files || (result ? [result] : [])

  if (files.length === 0) {
    return `## Google Drive Search Results

No files found.

---
`
  }

  const filesMarkdown = files
    .map((file: any) => {
      return `### ${file.name || 'Untitled'}

**ID:** ${file.id}
${file.webViewLink ? `**URL:** ${file.webViewLink}` : ''}
${file.size ? `**Size:** ${parseInt(file.size).toLocaleString()} bytes` : ''}
${file.modifiedTime ? `**Modified:** ${new Date(file.modifiedTime).toLocaleDateString()}` : ''}
`
    })
    .join('\n---\n\n')

  return `## Google Drive Search Results (${files.length} files)

${filesMarkdown}

---
`
}

export const GoogleDriveFindFileDisplay = createWorkflowToolComponent(
  GoogleDriveFindFileDisplayComponent,
  downloadGoogleDriveFindFile,
  googleDriveFindFileToMarkdown
)
